import React, { useContext } from 'react';
import { FormShellContext, SectionContext } from './context';

export type FormSectionProps = {
    /** stable id — used by the anchor and the error attribution */
    id: string;
    title: string;
    description?: React.ReactNode;
    icon?: React.ReactNode;
    /** controls pinned to the right of the section heading */
    extra?: React.ReactNode;
    /**
     * Drop the heading and the card around it: the group keeps its grid and its
     * name for assistive tech, but a page that is one card of its own has no
     * use for a card per group.
     */
    bare?: boolean;
    children: React.ReactNode;
};

/**
 * One titled group of fields. Renders a real <fieldset>/<legend> so screen
 * readers announce the group when focus enters any control inside it.
 */
const FormSection = ({ id, title, description, icon, extra, bare, children }: FormSectionProps) => {
    const shell = useContext(FormShellContext);
    const errorCount = shell?.sectionErrors[id] ?? 0;

    return (
        <SectionContext.Provider value={id}>
            <fieldset
                id={`section-${id}`}
                data-nf-section={id}
                className={`nf-section${bare ? ' nf-section--bare' : ''}${
                    errorCount > 0 ? ' nf-section--invalid' : ''
                }`}
                aria-describedby={description ? `section-${id}-desc` : undefined}
            >
                {/* the legend names the group for assistive tech and has to be
                    the fieldset's first child; the visible heading below is a
                    real <h2> so the page keeps a usable outline */}
                <legend className="nf-sr">{title}</legend>
                {!bare && (
                <div className="nf-section__head">
                    {icon && (
                        <span className="nf-section__ico" aria-hidden="true">
                            {icon}
                        </span>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h2 className="nf-section__title">{title}</h2>
                        {description && (
                            <p className="nf-section__desc" id={`section-${id}-desc`}>
                                {description}
                            </p>
                        )}
                    </div>
                    {extra}
                </div>
                )}
                <div className="nf-section__body">{children}</div>
            </fieldset>
        </SectionContext.Provider>
    );
};

export default FormSection;
